import type { Nft } from '@0xtorch/core'
import type {
  Erc20Token,
  Erc20TokenTransfer,
  InternalTransaction,
  LowerHex,
} from '../../../types'
import type { EvmAddress } from '../../../types/address'
import type { TransactionDecoded } from '../../../types/transactionDecoded'
import type { TransactionDetail } from '../../../types/transactionDetail'
import { decodeInput } from './decodeInput'
import { decodeLog } from './decodeLog'
import { filterMirrorErc20Transfers } from './filterMirrorErc20Transfers'
import { parseToErc20TokenTransfer } from './parseToErc20TokenTransfer'
import { parseToErc721TokenTransfer } from './parseToErc721TokenTransfer'
import { parseToErc1155TokenTransfer } from './parseToErc1155TokenTransfer'

const l2NativeTokenAddress = '0x000000000000000000000000000000000000800a'
const feeAddress = '0x0000000000000000000000000000000000008001'

type DecodeTransactionParameters = {
  transaction: TransactionDetail
  addresses: readonly EvmAddress[]
  erc20Tokens: readonly Erc20Token[]
  eventAbis: readonly {
    signature: LowerHex
    indexedCount: number
    abi: string
  }[]
  functionAbis: readonly string[]
  nfts: readonly Nft[]
}

export const decodeTransaction = ({
  transaction,
  addresses,
  erc20Tokens,
  eventAbis,
  functionAbis,
  nfts,
}: DecodeTransactionParameters): TransactionDecoded => {
  // parse fetched data
  const fromAddress = addresses.find(
    ({ address }) => address === transaction.from,
  )
  if (fromAddress === undefined) {
    throw new Error('Failed to get from address data')
  }
  const toAddress =
    transaction.to === undefined
      ? undefined
      : addresses.find(({ address }) => address === transaction.to)
  const eventAbiTexts = new Map<LowerHex, string[]>()
  for (const eventAbi of eventAbis) {
    const abiTexts = eventAbiTexts.get(eventAbi.signature) ?? []
    abiTexts.push(eventAbi.abi)
    eventAbiTexts.set(eventAbi.signature, abiTexts)
  }
  const erc20TokensMap = new Map(
    erc20Tokens.map((token) => [token.address, token]),
  )
  const nftsMap = new Map(nfts.map((nft) => [nft.id, nft]))

  // input data を decode → function 生成
  const transactionFunction =
    transaction.input.length >= 10
      ? decodeInput({
          input: transaction.input,
          addressAbi: toAddress?.abi,
          functionAbis,
        })
      : undefined

  // 既に decode されているものを除いて logs を decode
  const logs = transaction.logs.map((log) =>
    log.args !== undefined && log.eventName !== undefined
      ? log
      : decodeLog({
          log,
          addressAbi: addresses.find(({ address }) => address === log.address)
            ?.abi,
          eventAbis: eventAbiTexts.get(log.topics[0]) ?? [],
        }),
  )

  // ERC 20, 721, 1155 transfer の token data 代入
  // 別トークンの移動を鏡写しにした transfer (HyperEVM の wstHYPE など) は二重計上になるため除外する
  const erc20Transfers = filterMirrorErc20Transfers({
    chainId: transaction.chainId,
    transfers: transaction.erc20Transfers,
  }).map((transfer) =>
    parseToErc20TokenTransfer({ transfer, erc20Tokens: erc20TokensMap }),
  )
  const erc721Transfers = transaction.erc721Transfers.map((transfer) =>
    parseToErc721TokenTransfer({
      chainId: transaction.chainId,
      transfer,
      nfts: nftsMap,
    }),
  )
  const erc1155Transfers = transaction.erc1155Transfers.map((transfer) =>
    parseToErc1155TokenTransfer({
      chainId: transaction.chainId,
      transfer,
      nfts: nftsMap,
    }),
  )

  // chain 毎固有の条件に応じて ERC20 transfer をフィルタリング
  // l2NativeTokenAddress を持つチェーンの場合、l2NativeTokenAddress の tx fee 関連の transfer を除外・native transfer と同様の transfer を除外
  const filteredErc20Transfers: Erc20TokenTransfer[] = []
  let ignoredNativeTransfer = false
  let existL2NativeTokenTransfer = false
  for (const transfer of erc20Transfers) {
    // l2NativeTokenAddress 以外の transfer はそのまま追加
    if (transfer.token.address !== l2NativeTokenAddress) {
      filteredErc20Transfers.push(transfer)
      continue
    }
    existL2NativeTokenTransfer = true
    // tx fee に関する transfer は除外
    if (transfer.from === feeAddress || transfer.to === feeAddress) {
      continue
    }
    // それ以外の native/internal transaction と一致する transfer は除外
    if (
      !ignoredNativeTransfer &&
      transfer.from === transaction.from &&
      transfer.to === transaction.to &&
      transfer.value === transaction.value
    ) {
      ignoredNativeTransfer = true
      continue
    }
    filteredErc20Transfers.push(transfer)
  }

  // chain 毎固有の条件に応じて internal transaction をフィルタリング
  let filteredInternalTransactions: InternalTransaction[]
  if (existL2NativeTokenTransfer) {
    // l2NativeTokenAddress を持つチェーンの場合 internal transaction は除外する
    filteredInternalTransactions = []
  } else {
    filteredInternalTransactions = transaction.internalTransactions
  }

  // データを統合して TransactionDecoded に変形
  return {
    ...transaction,
    internalTransactions: filteredInternalTransactions,
    erc20Transfers: filteredErc20Transfers,
    erc721Transfers,
    erc1155Transfers,
    from: fromAddress,
    function: transactionFunction,
    logs,
    to: toAddress,
  }
}
